import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

function studioEnquiryParams(url) {
  const params = new URLSearchParams(url.search);
  const fragment = new URLSearchParams(url.hash.slice(1));
  for (const key of ['product', 'variant', 'url']) {
    if (fragment.has(key)) params.set(key, fragment.get(key));
  }
  return params;
}

function studioEnquiryLink(url, productUrl, variantTitle) {
  const context = studioEnquiryParams(url);
  const fragment = new URLSearchParams();
  for (const key of ['product', 'variant', 'url']) {
    if (context.has(key)) fragment.set(key, context.get(key));
    url.searchParams.delete(key);
  }
  if (variantTitle) fragment.set('variant', variantTitle);
  fragment.set('url', productUrl.pathname + productUrl.search);
  url.hash = fragment.toString();
  return url;
}

class StudioPurchaseHelp extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller = new AbortController();
    this.bundleVariants = new WeakMap();
    this.bundleTriggerChoices = new WeakMap();
    this.bundleDefaults = new WeakMap();
    this.formRoots = new WeakSet();
    this.highlightModes = new WeakMap();
    this.addonLists = new WeakSet();
    this.addonDisclosures = new WeakMap();
    this.nativeCards = new Map();
    this.addonAvailability = new Map();
    this.checkedAddonCards = new WeakSet();
    this.nativeCardTargets = new WeakSet();
    this.nativeCardObserver = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting) {
        this.nativeCardObserver.unobserve(entry.target);
        this.loadNativeCard(entry.target);
      }
    }, {rootMargin:'800px'});
    this.desktop = matchMedia('(min-width:750px)');
    this.layoutDesktop = matchMedia('(min-width:1000px)');
    this.layoutNodes = [];
    // A size change only needs a geometry update, not another app/form scan.
    this.layoutObserver = new ResizeObserver(() => this.scheduleLayout());
    this.formObservers = [];
    this.main = document.getElementById('MainContent');
    if (!this.main) return;
    const signal = this.controller.signal;
    // Browse-only recommendations use a native link, bypassing the app preview.
    this.main.addEventListener('click', event => {
      if (event.target instanceof Element && event.target.closest('.cs-recommendation-link,.cs-native-recommendation')) event.stopPropagation();
    }, {signal,capture:true});
    this.main.addEventListener('change', event => {
      if (!(event.target instanceof Element)) return;
      const widget=event.target.closest('lb-upsell-widget[widget="widget#ba"]');
      if (widget && event.target.matches('[trigger-card] select.lb-variant-picker')) this.bundleTriggerChoices.set(widget,event.target.value);
      const card=event.target.closest('[product-card]:not([trigger-card])');
      const state=widget && card && this.bundleDefaults.get(widget)?.get(card.getAttribute('lb-product-id'));
      if (!state) return;
      if (event.target.matches('input.lb-card-checkbox')) state.selected=event.target.checked;
      if (event.target.matches('select.lb-variant-picker')) state.variant=event.target.value;
    },{signal,capture:true});
    // Selleasy's completed cart response is authoritative. Bridge its independent
    // recommendation buttons to this theme's current cart event contract.
    window.addEventListener('lbSECartUpdated', event => this.recommendationCart(event), {signal});
    window.addEventListener('click', event => this.pumperCheckout(event), {signal,capture:true});
    this.observer = new MutationObserver(records => {
      const scope='.product-details,.cs-product-highlights,lb-upsell-widget';
      if (records.some(r => {
        const target=r.target instanceof Element ? r.target : r.target.parentElement;
        return target?.closest(scope) || [...r.addedNodes].some(node=>node instanceof Element && (node.matches(scope) || node.querySelector(scope)));
      })) this.schedule();
    });
    this.observer.observe(this.main, {subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['lb-variant-id']});
    this.main.addEventListener('change', () => this.schedule(), {signal});
    this.main.addEventListener('input', () => this.schedule(), {signal});
    this.main.addEventListener('quantity-selector:update', () => this.schedule(), {signal});
    this.desktop.addEventListener('change', () => this.schedule(), {signal});
    this.layoutDesktop.addEventListener('change', () => this.schedule(), {signal});
    this.main.addEventListener('click', event => {
      if (event.target instanceof Element && event.target.closest('lb-upsell-widget:not([widget="widget#pal"]) .lb-button')) this.recommendationPendingUntil=Date.now()+15000;
      if (event.target instanceof Element && event.target.closest('shopify-accelerated-checkout,more-payment-options-link')) this.expressAddons();
      if (this.desktop.matches && event.target instanceof Element && event.target.closest('.cs-product-feature-details>summary,.cs-category-details>summary')) event.preventDefault();
    }, {signal,capture:true});
    this.refresh();
    if (this.dataset.contact === 'true') {
      let attempts = 0;
      const initialiseEnquiry = () => {
        if (!this.isConnected || this.prefilled || attempts++ >= 60) return;
        this.enquiry();
        if (!this.prefilled) this.enquiryTimer = setTimeout(initialiseEnquiry, 500);
      };
      initialiseEnquiry();
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();this.observer?.disconnect();
    this.layoutObserver?.disconnect();
    this.nativeCardObserver?.disconnect();
    this.main?.style.removeProperty('--cs-desktop-continuation');
    this.main?.style.removeProperty('--cs-desktop-info-offset');
    for (const observer of this.formObservers || []) observer.disconnect();
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.layoutFrame);
    clearTimeout(this.checkoutTimer);
    clearTimeout(this.enquiryTimer);
  }
  schedule() {
    if (!this.frame) this.frame = requestAnimationFrame(() => {this.frame=0;this.refresh();});
  }
  scheduleLayout() {
    if (!this.layoutFrame) this.layoutFrame = requestAnimationFrame(() => {
      this.layoutFrame = 0;
      if (this.isConnected) this.desktopLayout();
    });
  }
  async loadNativeCard(target) {
    const handle=target.getAttribute('lb-product-handle');
    const source=document.querySelector('template[data-studio-collection-card]');
    if (!handle || !source) return;
    try {
      if (!this.nativeCards.has(handle)) {
        const url=new URL((window.Shopify?.routes?.root || '/')+'products/'+encodeURIComponent(handle),location.origin);
        url.searchParams.set('section_id',source.dataset.sectionId);
        this.nativeCards.set(handle,fetch(url,{signal:this.controller.signal}).then(async response=>{
          if (!response.ok) throw new Error('Recommendation card unavailable');
          const doc=new DOMParser().parseFromString(await response.text(),'text/html');
          const template=doc.querySelector('template[data-studio-collection-card]');
          if (!template?.content.querySelector('product-card')) throw new Error('Recommendation card missing');
          // Section Rendering can return component styles outside the template.
          for (const style of doc.querySelectorAll('style[data-section-stylesheet],link[rel="stylesheet"]')) {
            const key=style.getAttribute('href') || style.textContent;
            this.nativeCardStyles ||= new Set();
            if (!this.nativeCardStyles.has(key)) {this.nativeCardStyles.add(key);document.head.append(style.cloneNode(true));}
          }
          return template;
        }));
      }
      const template=await this.nativeCards.get(handle);
      if (template.dataset.productAvailable==='false') {
        target.dataset.csUnavailable='true';
        target.hidden=true;
        this.schedule();
        return;
      }
      if (!target.isConnected || this.controller.signal.aborted || target.querySelector('.cs-native-recommendation')) return;
      const host=document.createElement('div');
      host.className='cs-native-recommendation resource-list__item';
      host.append(template.content.cloneNode(true));
      // Each app-selected product has its own unique card and control IDs.
      const ids=new Map();
      for (const element of host.querySelectorAll('[id]')) {
        const old=element.id;const next=old+'-cpac-'+handle;
        ids.set(old,next);element.id=next;
      }
      for (const element of host.querySelectorAll('[for],[aria-labelledby],[aria-describedby],[aria-controls],[href^="#"]')) {
        for (const attr of ['for','aria-labelledby','aria-describedby','aria-controls']) if (element.hasAttribute(attr)) element.setAttribute(attr,element.getAttribute(attr).split(' ').map(id=>ids.get(id)||id).join(' '));
        const href=element.getAttribute('href');if (href?.startsWith('#') && ids.has(href.slice(1))) element.setAttribute('href','#'+ids.get(href.slice(1)));
      }
      target.append(host);
    } catch (error) {
      // Keep the original linked card usable if a fragment request fails.
      if (error.name!=='AbortError') this.nativeCards.delete(handle);
    }
  }
  nativeRecommendations() {
    for (const widget of this.main.querySelectorAll('lb-upsell-widget[widget="widget#cpac"]')) {
      let count=0;
      [...widget.querySelectorAll('lb-upsell-long-card')].forEach(card=>{
        const hidden=card.dataset.csUnavailable==='true' || count>=7;
        if (!hidden) count++;
        if (card.hidden !== hidden) card.hidden=hidden;
      });
    }
    for (const card of this.main.querySelectorAll('lb-upsell-widget[widget="widget#cpac"] lb-upsell-long-card')) {
      if (card.hidden) continue;
      if (card.querySelector('.cs-native-recommendation')) continue;
      if (this.nativeCards.has(card.getAttribute('lb-product-handle'))) {this.loadNativeCard(card);continue;}
      if (this.nativeCardTargets.has(card)) continue;
      this.nativeCardTargets.add(card);
      this.nativeCardObserver.observe(card);
    }
  }
  checkAddonAvailability() {
    for (const card of this.main.querySelectorAll('lb-upsell-widget:is([widget="widget#pal"],[widget="widget#ba"]) [lb-product-handle]:not([trigger-card])')) {
      if (this.checkedAddonCards.has(card)) continue;
      const handle=card.getAttribute('lb-product-handle');
      if (!handle) continue;
      this.checkedAddonCards.add(card);
      if (!this.addonAvailability.has(handle)) {
        const url=(window.Shopify?.routes?.root || '/')+'products/'+encodeURIComponent(handle)+'.js';
        this.addonAvailability.set(handle,fetch(url,{signal:this.controller.signal}).then(response=>{
          if (!response.ok) throw new Error('Product availability unavailable');
          return response.json();
        }));
      }
      this.addonAvailability.get(handle).then(product=>{
        if (!card.isConnected || this.controller.signal.aborted || product.available!==false) return;
        card.dataset.csUnavailable='true';
        const checkbox=card.querySelector('input.lb-card-checkbox');
        if (checkbox?.checked) checkbox.click();
        card.hidden=true;
      }).catch(()=>{});
    }
  }
  bundlePresentation() {
    for (const widget of this.main.querySelectorAll('lb-upsell-widget[widget="widget#ba"]')) {
      for (const compare of widget.querySelectorAll('.lb-compare-cont')) {
        const price=compare.querySelector('.lb-price-compare-price');
        let label=compare.querySelector('.cs-price-rrp');
        if (price?.textContent.trim() && !label) {
          label=document.createElement('span');
          label.className='cs-price-rrp';
          label.textContent='UVP';
          compare.append(label);
        } else if (!price?.textContent.trim() && label) label.remove();
      }
      let seen=this.bundleDefaults.get(widget);
      if (!seen) {seen=new Map();this.bundleDefaults.set(widget,seen);}
      for (const card of widget.querySelectorAll('[product-card]:not([trigger-card])')) {
        if (card.dataset.csUnavailable==='true') continue;
        const checkbox=card.querySelector('input.lb-card-checkbox');
        const key=card.getAttribute('lb-product-id');
        if (!checkbox || checkbox.disabled || !key) continue;
        const select=card.querySelector('select.lb-variant-picker');
        if (!seen.has(key)) seen.set(key,{selected:true,variant:select?.value});
        const state=seen.get(key);
        if (select && state.variant && select.value!==state.variant && [...select.options].some(o=>o.value===state.variant && !o.disabled)) {
          select.value=state.variant;
          select.dispatchEvent(new Event('change',{bubbles:true}));
        }
        if (checkbox.checked!==state.selected) checkbox.click();
      }
      // Preserve Stencil text-node identity so repeated cart adds keep working.
      for (const [selector,oldText,newText] of [
        ['.lb-bundle-title','Ergänzende Produkte auswählen','Wird oft zusammen gekauft'],
        ['.lb-bundle-subTitle','Wähle passende Varianten und nur die Produkte, die du zusätzlich benötigst.','Passende Produkte sind vorausgewählt. Du kannst die Auswahl jederzeit ändern.'],
        ['.lb-button','In den Warenkorb legen','Alles in den Warenkorb']
      ]) {
        const element=widget.querySelector(selector);
        if (!element) continue;
        for (const node of element.childNodes) if (node.nodeType===Node.TEXT_NODE && node.nodeValue.trim()===oldText) node.nodeValue=newText;
      }
    }
  }
  recommendationCart(event) {
    const {cart,isAtc,response}=event.detail || {};
    if (!isAtc || Date.now()>(this.recommendationPendingUntil || 0) || !Array.isArray(cart?.items) || response?.status>=400) return;
    this.recommendationPendingUntil=0;
    this.dispatchEvent(new CartLinesUpdateEvent({
      action:'add',context:'product',lines:[],
      promise:Promise.resolve({
        cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail:{items:cart.items,itemCount:cart.item_count,source:'studio-recommendations',sections:response?.sections,didError:false}
      })
    }));
  }
  refresh() {
    // Read the previous frame's geometry before this pass changes the DOM.
    this.desktopLayout();
    this.checkAddonAvailability();
    this.nativeRecommendations();
    const detailSale=this.main.querySelector('[data-cs-detail-sale]');
    const saleContext=this.main.querySelector('[data-cs-pumper-context]');
    if (detailSale && saleContext) {
      const regular=Number(saleContext.dataset.comparePrice);
      const price=Number(saleContext.dataset.unitPrice);
      const discountPercent=regular>price && regular>0 ? Math.floor((regular-price)*100/regular) : 0;
      const onSale=discountPercent>0;
      detailSale.hidden=!onSale;
      const text=onSale ? '−'+discountPercent+' %' : '';
      if (detailSale.textContent!==text) detailSale.textContent=text;
    }
    for (const card of this.main.querySelectorAll('lb-upsell-widget[widget="widget#cpac"] lb-upsell-long-card')) {
      const handle=card.getAttribute('lb-product-handle');
      const title=card.querySelector('.lb-card-title')?.textContent.trim();
      const surface=card.querySelector('.lb-long-card');
      if (!handle || !title || !surface) continue;
      for (const compare of card.querySelectorAll('.lb-compare-cont')) {
        const price=compare.querySelector('.lb-price-compare-price');
        let label=compare.querySelector('.cs-price-rrp');
        if (price?.textContent.trim() && !label) {
          label=document.createElement('span');
          label.className='cs-price-rrp';
          label.textContent='UVP';
          compare.append(label);
        } else if (!price?.textContent.trim() && label) label.remove();
      }
      let link=surface.querySelector('.cs-recommendation-link');
      if (!link) {
        link=document.createElement('a');
        link.className='cs-recommendation-link';
        surface.append(link);
      }
      const root=window.Shopify?.routes?.root || '/';
      const href=root+'products/'+encodeURIComponent(handle);
      if (link.getAttribute('href')!==href) link.setAttribute('href',href);
      if (link.getAttribute('aria-label')!==title) link.setAttribute('aria-label',title);
      for (const preview of card.querySelectorAll('lb-card-image,[role="link"]')) {
        if (preview.getAttribute('tabindex')!=='-1') preview.setAttribute('tabindex','-1');
      }
    }
    for (const rating of this.main.querySelectorAll('lb-upsell-widget[widget="widget#cpac"] .lb-reviews-rating')) {
      const value=Number(rating.querySelector(".lb-reviews-rating-value")?.textContent);
      const container=rating.querySelector(".lb-reviews-rating-stars");
      if (!container || !Number.isFinite(value)) continue;
      const score=Math.round(Math.max(0,Math.min(5,value))*2)/2;
      if (container.dataset.csStarScore===String(score) && container.querySelector(".cs-recommendation-stars")) continue;
      let stars=container.querySelector(".cs-recommendation-stars");
      if (!stars) {
        stars=document.createElement("span");
        stars.className="cs-recommendation-stars jdgm-prev-badge__stars";
        stars.setAttribute("aria-hidden","true");
        container.append(stars);
      }
      stars.replaceChildren(...Array.from({length:5},(_,index)=>{
        const star=document.createElement("span");
        star.className="jdgm-star "+(score>=index+1 ? "jdgm--on" : score>index ? "jdgm--half" : "jdgm--off");
        return star;
      }));
      container.dataset.csStarScore=String(score);
    }
    this.extraQuantities();
    this.productPresentation();
    if (this.dataset.contact === 'true') {this.enquiry();return;}
    for (const wrapper of this.main.querySelectorAll('.product-details .quantity-selector-wrapper')) {
      const label=wrapper.querySelector('input[name="quantity"]')?.getAttribute('aria-label');
      if (label && wrapper.dataset.csQuantityLabel !== label) wrapper.dataset.csQuantityLabel=label;
    }
    this.bundlePresentation();
    for (const control of this.main.querySelectorAll(".product-details quantity-selector-component")) control.syncQuantityDropdown?.();
    this.highlights();
    this.addons();
    const picker = this.main.querySelector('variant-picker[data-template-product-match="true"]');
    let variant;
    try {variant=JSON.parse(picker?.querySelector('script[type="application/json"]')?.textContent || '{}');} catch {return;}
    const nativeId = String(variant.id || this.main.querySelector('.product-details input[name="id"]')?.value || '');
    if (!nativeId) return;
    const context = this.main.querySelector('[data-cs-pumper-context]');
    const productId = picker?.dataset.productId || context?.closest('product-price')?.dataset.productId;
    for (const widget of this.main.querySelectorAll('lb-upsell-widget')) {
      // Only the bundle's trigger card follows the main variant. Add-on variants stay independent.
      const card = widget.querySelector('[trigger-card]');
      const select = card?.querySelector('select.lb-variant-picker');
      if (!card || !select || (productId && card.getAttribute('lb-product-id') !== productId)) continue;
      if (this.bundleVariants.get(widget)!==nativeId) this.bundleTriggerChoices.set(widget,nativeId);
      const desired=this.bundleTriggerChoices.get(widget) || nativeId;
      if (![...select.options].some(option => option.value === desired)) continue;
      this.bundleVariants.set(widget,nativeId);
      if (select.value !== desired) {
        select.value=desired;
        select.dispatchEvent(new Event('change',{bubbles:true}));
      }
    }
    for (const link of this.main.querySelectorAll('[data-studio-product-advice]')) {
      const url = new URL(link.href,location.origin);
      const productUrl = new URL(location.pathname,location.origin);
      productUrl.searchParams.set('variant',nativeId);
      studioEnquiryLink(url, productUrl, variant.title);
      if (link.href !== url.href) link.href=url.href;
    }
    // Selleasy owns its button's children, including loading and success states.
    // Replacing them breaks the app's next virtual-DOM update and repeat adds.
    if (document.documentElement.lang === 'de') {
      for (const amount of this.main.querySelectorAll('lb-upsell-widget .lb-price-sale-price,lb-upsell-widget .lb-price-regular-price,lb-upsell-widget .lb-price-compare-price,[data-testid="sticky-price-display"] .price,[data-testid="sticky-price-display"] .compare-at-price')) {
        for (const node of amount.childNodes) if (node.nodeType === Node.TEXT_NODE) {
          const formatted=node.textContent.replace(/^\s*€\s*([\d.]+,\d{2})\s*$/, '$1 €');
          if (formatted !== node.textContent) node.textContent=formatted;
        }
      }
    }
  }
  extraQuantities() {
    const enhance=(root,input,commit,label)=>{
      if (!root || !input) return;
      let select=root.querySelector(':scope>.cs-extra-quantity-select');
      if (!select) {
        select=document.createElement('select');
        select.className='cs-extra-quantity-select';select.setAttribute('aria-label',label);
        root.append(select);root.classList.add('cs-extra-quantity');
        select.addEventListener('change',()=>{
          if(select.value==='custom') {root.classList.add('cs-extra-custom');input.focus();input.select();return;}
          root.classList.remove('cs-extra-custom');input.value=select.value;commit();this.schedule();
        });
        input.addEventListener('blur',()=>this.schedule());
      }
      const min=Number(input.min)||1,step=Number(input.step)||1,max=input.max?Number(input.max):Infinity;
      const key=[min,step,max].join(':');
      if(select.dataset.constraints!==key){
        const options=[];for(let q=min;q<50&&q<=max;q+=step)options.push(new Option(String(q),String(q)));
        if(max>=50)options.push(new Option('50+','custom'));
        select.replaceChildren(...options);select.dataset.constraints=key;
      }
      select.disabled=input.disabled;
      if(document.activeElement!==input){
        const value=String(input.value||min),custom=![...select.options].some(o=>o.value===value);
        root.classList.toggle('cs-extra-custom',custom);select.value=custom?'custom':value;
      }
    };
    for(const root of this.main.querySelectorAll('lb-quantity-picker .lb-qty-picker')){
      const input=root.querySelector('input.lb-qty-count');
      enhance(root,input,()=>{input.dispatchEvent(new Event('input',{bubbles:true}));input.dispatchEvent(new Event('change',{bubbles:true}));},'Zubehörmenge');
    }
    const native=this.main.querySelector('.product-details quantity-selector-component');
    const source=native?.querySelector('input[name="quantity"]');
    const bar=this.main.querySelector('.sticky-add-to-cart__bar');
    if(!source||!bar)return;
    let root=bar.querySelector('.cs-sticky-quantity');
    if(!root){
      root=document.createElement('div');root.className='cs-sticky-quantity';
      const input=document.createElement('input');input.type='number';input.setAttribute('aria-label','Menge im Schnellwarenkorb');
      root.append(input);bar.insertBefore(root,bar.querySelector('.sticky-add-to-cart__button'));
      input.addEventListener('change',()=>{
        if(!input.reportValidity())return;
        const native=this.main.querySelector(".product-details quantity-selector-component");const source=native?.querySelector("input[name=quantity]");if(!source)return;source.value=input.value;native.dispatchEvent(new CustomEvent('studio:quantity-select',{bubbles:true,detail:{quantity:Number(input.value)}}));native.onQuantityChange();native.updateButtonStates();this.schedule();
      });
      input.addEventListener('input',()=>{if(input.validity.valid)input.dispatchEvent(new Event('change',{bubbles:true}));});
    }
    const input=root.querySelector('input');
    if(document.activeElement!==input)input.value=source.value;
    input.min=source.min;input.step=source.step;input.max=native.getEffectiveMax()??'';input.disabled=source.disabled;
    enhance(root,input,()=>input.dispatchEvent(new Event('change',{bubbles:true})),'Menge im Schnellwarenkorb');
  }
  productPresentation() {
    const description=this.main.querySelector('.text-block[class*="__text_zt8cU6"] h2');
    if(description && description.id!=='clendo-product-description')description.id='clendo-product-description';
    const highlights=this.main.querySelector('.cs-product-highlights');
    if (!highlights) return;
    for (const badge of highlights.querySelectorAll('.jdgm-prev-badge[data-number-of-reviews]')) {
      const count=badge.getAttribute('data-number-of-reviews');
      const text=badge.querySelector('.jdgm-prev-badge__text');
      if (text && /^\d+$/.test(count)) {
        const label=`(${count})`;
        if (text.textContent!==label) text.textContent=label;
        text.setAttribute('aria-label',`${count} Bewertungen`);
      }
    }
    const picker=this.main.querySelector('variant-picker[data-template-product-match="true"]');
    const target=highlights.querySelector('.cs-product-feature-details')?.parentElement;
    if (!picker || !target) return;
    if (!this.variantHome || !this.variantHome.isConnected) {
      // The parser-time placement keeps a stable home for mobile and editor updates.
      this.variantHome=picker.closest('product-component')?.querySelector('template[data-cs-variant-home]');
      if (!this.variantHome) {
        this.variantHome=document.createComment('Original variant picker position');
        picker.before(this.variantHome);
      }
    }
    const anchor=this.layoutDesktop.matches ? target : this.variantHome.nextSibling;
    const parent=this.layoutDesktop.matches ? target.parentElement : this.variantHome.parentElement;
    if (picker.parentElement!==parent || (this.layoutDesktop.matches && picker.nextSibling!==target)) {
      // Only move the native picker; app widgets and purchase forms retain their roots.
      if (parent.moveBefore) parent.moveBefore(picker,anchor);
      else parent.insertBefore(picker,anchor);
    }
  }
  desktopLayout() {
    if (this.main.querySelector(".cs-cross-selling")) {
      this.layoutObserver.disconnect(); this.layoutNodes=[];
      this.main.style.removeProperty("--cs-desktop-continuation");
      this.main.style.removeProperty("--cs-desktop-info-offset");
      return;
    }
    if (!this.layoutDesktop.matches && !this.layoutNodes.length) return;
    const grid=this.main.querySelector('.product-information__grid.cs-product-three');
    const nodes=['.product-details','.product-information__media','.cs-product-highlights'].map(selector=>grid?.querySelector(`:scope>${selector}`));
    if (!this.layoutDesktop.matches || !CSS.supports('grid-template-rows','subgrid') || !this.main.querySelector(':scope>[id$="__section_QEQQmr"]') || nodes.some(node=>!node)) {
      this.layoutObserver.disconnect();this.layoutNodes=[];
      this.main.style.removeProperty('--cs-desktop-continuation');
      this.main.style.removeProperty('--cs-desktop-info-offset');
      return;
    }
    if (nodes.some((node,index)=>node!==this.layoutNodes[index])) {
      this.layoutObserver.disconnect();this.layoutNodes=nodes;
      for (const node of nodes) this.layoutObserver.observe(node);
    }
    const outerHeight=node=>{
      const style=getComputedStyle(node);
      return node.getBoundingClientRect().height+(parseFloat(style.marginBlockStart)||0)+(parseFloat(style.marginBlockEnd)||0);
    };
    // Reserve only the height actually needed below the gallery/highlights.
    // A shorter purchase column can continue above the left-column boundary.
    const remainder=outerHeight(nodes[0])-Math.max(outerHeight(nodes[1]),outerHeight(nodes[2]));
    for (const [property,value] of [['--cs-desktop-continuation',Math.max(0,remainder)],['--cs-desktop-info-offset',Math.min(0,remainder)]]) {
      const size=`${Math.round(value*100)/100}px`;
      if (this.main.style.getPropertyValue(property)!==size) this.main.style.setProperty(property,size);
    }
  }
  highlights() {
    for (const details of this.main.querySelectorAll('.cs-product-feature-details,.cs-category-details')) {
      const desktop=this.desktop.matches;
      if (this.highlightModes.get(details) === desktop) continue;
      const initialized=this.highlightModes.has(details);
      this.highlightModes.set(details,desktop);
      if (desktop || initialized || details.matches(".cs-product-feature-details")) details.open=desktop;
      const summary=details.querySelector('summary');
      if (!summary) continue;
      if (desktop) {
        summary.setAttribute('tabindex','-1');
        // Preserve the native disclosure semantics; heading is not a valid summary role.
        summary.removeAttribute('role');
        summary.removeAttribute('aria-level');
      } else {
        summary.removeAttribute('tabindex');
        summary.removeAttribute('role');
        summary.removeAttribute('aria-level');
      }
    }
  }
  addons() {
    // Pumper inserts its plain container before the form after app blocks hydrate.
    // Keep accessories before quantity tiers without reconnecting the native accessory widget.
    for (const form of this.main.querySelectorAll('.product-details product-form-component')) {
      const tiers=form.querySelector(':scope>#prvw__bundle_container');
      const accessories=form.querySelector(':scope>.cs-accessory-options, :scope>.shopify-app-block:has(.lb-addon-cont)');
      if (tiers && accessories && (tiers.compareDocumentPosition(accessories) & Node.DOCUMENT_POSITION_FOLLOWING)) accessories.after(tiers);
    }
    for (const container of this.main.querySelectorAll('.product-details .lb-addon-cont')) this.addonDisclosure(container);
    this.expressAddons();
    for (const list of this.main.querySelectorAll('.product-details .lb-addon-cont>.lb-list')) {
      if (this.addonLists.has(list) || list.children.length < 2) continue;
      this.addonLists.add(list);
      const head=list.parentElement.querySelector('.lb-addon-head');
      const existing=head?.querySelector('.cs-addon-navigation') || list.parentElement.querySelector(':scope>.cs-addon-navigation');
      const controls=existing || document.createElement('div');
      controls.className='cs-addon-navigation';
      const previous=document.createElement('button'), next=document.createElement('button'), count=document.createElement('span');
      previous.type=next.type='button';
      previous.textContent='‹';next.textContent='›';
      previous.setAttribute('aria-label',this.dataset.previousAddon);
      next.setAttribute('aria-label',this.dataset.nextAddon);
      count.className='cs-addon-position';
      controls.replaceChildren(previous,count,next);
      if (head && controls.parentElement !== head) head.append(controls);
      const cards=()=>[...list.children].filter(card=>card.matches('[product-card]'));
      const index=()=> {
        const bounds=list.getBoundingClientRect(), rtl=getComputedStyle(list).direction==='rtl';
        let nearest=0,distance=Infinity;
        cards().forEach((card,i)=>{
          const rect=card.getBoundingClientRect();
          const delta=Math.abs(rtl ? bounds.right-rect.right : rect.left-bounds.left);
          if(delta<distance){distance=delta;nearest=i;}
        });
        return nearest;
      };
      const update=()=>{
        const current=index(), total=cards().length;
        count.textContent=(current+1)+' / '+total;
        previous.disabled=current===0;next.disabled=current===total-1;
      };
      const move=delta=>{
        const items=cards(), current=Math.max(0,Math.min(items.length-1,index()+delta));
        const target=items[current], bounds=list.getBoundingClientRect(), rect=target?.getBoundingClientRect();
        if(!rect) return;
        const rtl=getComputedStyle(list).direction==='rtl';
        list.scrollBy({left:rtl ? rect.right-bounds.right : rect.left-bounds.left,behavior:'instant'});
      };
      const signal=this.controller.signal;
      previous.addEventListener('click',()=>move(-1),{signal});
      next.addEventListener('click',()=>move(1),{signal});
      list.addEventListener('scroll',update,{signal,passive:true});
      this.desktop.addEventListener('change',update,{signal});
      update();
    }
  }
  expressAddons() {
    // Shopify's product-page express checkout accepts these additional-item fields.
    // Selleasy continues to own the regular add-to-cart action and its app components.
    for (const component of this.main.querySelectorAll('.product-details product-form-component')) {
      const form=component.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) continue;
      const items=new Map();
      for (const card of component.querySelectorAll('.lb-addon-cont [product-card]')) {
        if (!card.querySelector('input.lb-card-checkbox:checked')) continue;
        const id=card.getAttribute('lb-variant-id');
        const quantity=Number(card.querySelector('input.lb-qty-count')?.value || 1);
        if (!/^\d+$/.test(id || '') || !Number.isSafeInteger(quantity) || quantity < 1) continue;
        items.set(id,(items.get(id) || 0)+quantity);
      }
      const signature=JSON.stringify([...items]);
      let fields=form.querySelector(':scope>[data-cs-express-addons]');
      if (!items.size) {fields?.remove();continue;}
      if (fields?.dataset.csExpressAddons === signature) continue;
      if (!fields) {
        fields=document.createElement('div');
        fields.hidden=true;
        form.append(fields);
      }
      fields.dataset.csExpressAddons=signature;
      const inputs=[];
      let index=1;
      for (const [id,quantity] of items) {
        for (const [key,value] of [['id',id],['quantity',String(quantity)]]) {
          const input=document.createElement('input');
          input.type='hidden';
          input.name=`shopify-additional-items[${index}][${key}]`;
          input.setAttribute('value',value);
          inputs.push(input);
        }
        index++;
      }
      fields.replaceChildren(...inputs);
    }
  }
  pumperCheckout(event) {
    if (!(event.target instanceof Element)) return;
    const link=event.target.closest('more-payment-options-link');
    const component=link?.closest('product-form-component');
    if (!component || !this.main.contains(component) || !component.querySelector('#prvw__bundle_container')) return;
    const selected=[...component.querySelectorAll('.lb-addon-cont [product-card]:has(input.lb-card-checkbox:checked)')];
    if (!selected.length) return;
    const button=[...component.querySelectorAll('form[data-type="add-to-cart-form"] button[name="add"]')].find(node=>node.getClientRects().length);
    if (!button || button.disabled) return;
    event.preventDefault();event.stopImmediatePropagation();
    if (this.checkoutPending) return;
    this.checkoutPending=true;
    const expected=selected.map(card=>card.getAttribute('lb-variant-id'));
    const complete=event=>{
      const items=event.detail?.resource?.items;
      if (!Array.isArray(items) || !expected.every(id=>items.some(item=>String(item.variant_id || item.id)===id))) return;
      cleanup();
      window.location.assign((window.Shopify?.routes?.root || '/')+'checkout');
    };
    const cleanup=()=>{
      clearTimeout(this.checkoutTimer);
      document.removeEventListener('cart:update',complete);
      this.checkoutPending=false;
    };
    document.addEventListener('cart:update',complete,{signal:this.controller.signal});
    // Let Pumper and Selleasy build the discounted cart together before leaving it.
    // If an app fails, keep its normal cart/error interface available for retry.
    this.checkoutTimer=setTimeout(cleanup,12000);
    button.click();
  }
  addonDisclosure(container) {
    const head=container.querySelector('.lb-addon-head'), list=container.querySelector(':scope>.lb-list');
    if (!head || !list || !list.children.length) return;
    let state=this.addonDisclosures.get(container);
    if (!state || !head.contains(state.summary)) {
      head.querySelector('.cs-addon-toggle')?.remove();
      const summary=head.querySelector('.cs-addon-summary') || document.createElement('div');
      summary.className='cs-addon-summary';
      const label=document.createElement('strong'), status=document.createElement('span');
      label.textContent=this.dataset.addonLabel;label.setAttribute('role','heading');label.setAttribute('aria-level','2');
      status.className='cs-addon-status';status.setAttribute('aria-live','polite');status.setAttribute('aria-atomic','true');
      summary.replaceChildren(label,status);
      if (!head.contains(summary)) head.prepend(summary);
      state={summary,status};this.addonDisclosures.set(container,state);
      container.addEventListener('input',()=>this.schedule(),{signal:this.controller.signal});
      container.addEventListener('click',()=>this.schedule(),{signal:this.controller.signal});
    }
    const selected=list.querySelectorAll('input.lb-card-checkbox:checked').length;
    const description=(selected ? this.dataset.addonSelected : this.dataset.addonOptional).replace('__COUNT__',String(selected));
    if (state.status.textContent !== description) state.status.textContent=description;
    const disclosureStatus=container.closest('.cs-accessory-options')?.querySelector('[data-cs-accessory-status]');
    if (disclosureStatus && disclosureStatus.textContent !== description) disclosureStatus.textContent=description;
    container.classList.remove('cs-addon-collapsed');
    container.classList.toggle('cs-addon-selected',selected>0);
    for (const card of list.querySelectorAll('[product-card]')) {
      const checkbox=card.querySelector('input.lb-card-checkbox');
      const title=card.querySelector('.lb-card-title')?.textContent.trim();
      if (checkbox && title) {
        const label=this.dataset.addonCheckbox.replace('__PRODUCT__',title);
        if (checkbox.getAttribute('aria-label') !== label) checkbox.setAttribute('aria-label',label);
      }
    }
  }
  enquiry() {
    const labelFields = root => {
      for (const message of root.querySelectorAll('textarea[name="custom#nachricht"]')) {
        const label=message.parentElement?.querySelector('label');
        if (!message.id && label?.htmlFor) message.id=label.htmlFor;
      }
    };
    labelFields(this.main);
    for (const embed of this.main.querySelectorAll('shopify-forms-embed')) {
      const root=embed.shadowRoot;
      if (!root) continue;
      labelFields(root);
      if (!this.formRoots.has(root)) {
        this.formRoots.add(root);
        const observer=new MutationObserver(()=>{labelFields(root);if (!this.prefilled) this.enquiry();});
        observer.observe(root,{subtree:true,childList:true});this.formObservers.push(observer);
      }
    }
    const params = studioEnquiryParams(new URL(location.href));
    const product=(params.get('product')||'').slice(0,250), variant=(params.get('variant')||'').slice(0,150);
    if (!product) return;
    const summary=[product,variant !== 'Default Title' ? variant : ''].filter(Boolean).join(' · ');
    const panel=this.querySelector('.studio-enquiry-context');
    if (panel) {
      panel.hidden=false;panel.querySelector('[data-enquiry-summary]').textContent=summary;
      const link=panel.querySelector('[data-enquiry-product]');
      const path=params.get('url')||'';
      if (path.startsWith('/products/') && !path.startsWith('//')) {
        const url=new URL(path,location.origin);
        if(url.origin===location.origin){link.href=url.href;link.textContent=product;link.hidden=false;}
      }
    }
    if (this.prefilled) return;
    const fill = root => {
      const message=root.querySelector('textarea[name="custom#nachricht"],textarea[name="contact[body]"]');
      if (!message || message.value || this.prefilled) return;
      const introduction=this.dataset.message.replace('__PRODUCT__',product).replace('__VARIANT__',variant === 'Default Title' ? '' : variant);
      Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype,'value').set.call(message,introduction+'\n\n');
      message.dispatchEvent(new Event('input',{bubbles:true,composed:true}));
      message.dispatchEvent(new Event('change',{bubbles:true,composed:true}));
      this.prefilled=true;
    };
    fill(this.main);
    for (const embed of this.main.querySelectorAll('shopify-forms-embed')) {
      if (!embed.shadowRoot) continue;
      fill(embed.shadowRoot);
      if (!this.formRoots.has(embed.shadowRoot) && !this.prefilled) {
        this.formRoots.add(embed.shadowRoot);
        const observer=new MutationObserver(()=>fill(embed.shadowRoot));
        observer.observe(embed.shadowRoot,{subtree:true,childList:true});this.formObservers.push(observer);
      }
    }
  }
}
if (!customElements.get('studio-purchase-help')) customElements.define('studio-purchase-help',StudioPurchaseHelp);
